import React, { useState, useRef, useEffect } from 'react';
import { Mic, Square, Send, X, Play, Pause } from 'lucide-react';
import { getImageUrl } from '../../utils/imageUtils';

export const VoiceRecorderButton = ({ onStartRecording, isRecording }) => (
  <button onClick={onStartRecording}
    className={`h-8 w-8 p-0 flex items-center justify-center rounded-lg transition-all ${
      isRecording
        ? 'text-red-400 bg-red-500/20 animate-pulse'
        : 'text-slate-400 hover:text-white hover:bg-slate-700'
    }`} data-testid="voice-record-btn">
    <Mic className="w-4 h-4" />
  </button>
);

export const VoiceRecorderBar = ({ onSend, onCancel }) => {
  const [seconds, setSeconds] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    let stream;
    const startRec = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
        chunksRef.current = [];
        recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
        recorder.start();
        setMediaRecorder(recorder);
      } catch {
        onCancel();
      }
    };
    startRec();
    return () => { stream?.getTracks().forEach(t => t.stop()); };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleStop = () => {
    if (!mediaRecorder) return;
    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
      onSend(blob);
    };
    mediaRecorder.stop();
    mediaRecorder.stream?.getTracks().forEach(t => t.stop());
  };

  const handleCancel = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
      mediaRecorder.stream?.getTracks().forEach(t => t.stop());
    }
    onCancel();
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="flex items-center gap-3 w-full bg-slate-800 border border-red-500/30 rounded-xl px-4 py-3 animate-[fadeIn_0.2s_ease-out]" data-testid="voice-recorder-bar">
      <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
      <span className="text-red-400 text-sm font-mono font-medium">{fmt(seconds)}</span>
      <div className="flex-1 flex items-center gap-1">
        {Array.from({ length: 20 }).map((_, i) => (
          <div key={i} className="w-1 bg-red-500/40 rounded-full animate-pulse"
            style={{ height: `${8 + Math.random() * 16}px`, animationDelay: `${i * 0.05}s` }} />
        ))}
      </div>
      <button onClick={handleCancel} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors" data-testid="voice-cancel-btn">
        <X className="w-4 h-4" />
      </button>
      <button onClick={handleStop} className="p-2 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700 transition-all" data-testid="voice-send-btn">
        <Send className="w-4 h-4" />
      </button>
    </div>
  );
};

export const VoiceMessagePlayer = ({ voiceUrl }) => {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  const url = voiceUrl?.startsWith('http') ? voiceUrl : getImageUrl(voiceUrl);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); }
    else { audioRef.current.play(); }
    setPlaying(!playing);
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return '0:00';
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  };

  return (
    <div className="flex items-center gap-2.5 bg-slate-900/50 border border-slate-700/50 rounded-xl px-3 py-2 min-w-[200px]" data-testid="voice-player">
      <audio ref={audioRef} src={url} preload="metadata"
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onTimeUpdate={(e) => setProgress(e.target.duration ? (e.target.currentTime / e.target.duration) * 100 : 0)}
        onEnded={() => { setPlaying(false); setProgress(0); }} />
      <button onClick={togglePlay}
        className="w-8 h-8 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400 hover:bg-indigo-500/30 transition-all flex-shrink-0">
        {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-200" style={{ width: `${progress}%` }} />
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[10px] text-slate-500 font-mono">{fmt(audioRef.current?.currentTime || 0)}</span>
          <span className="text-[10px] text-slate-500 font-mono">{fmt(duration)}</span>
        </div>
      </div>
      <Mic className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />
    </div>
  );
};
